/**
 * Demo Automations
 *
 * Pre-configured automations shown to new users with no existing automations.
 * Each demo stores raw task data (copy-paste from DB) plus display metadata.
 *
 * To add a new demo:
 * 1. Run the automation in the platform
 * 2. Copy the task JSON from the DB
 * 3. Add it below with display metadata (id, name, website, favicon, description)
 */

export const DEMO_AUTOMATIONS = [
  {
    id: "demo-yc-directory",
    name: "YC Startup Directory",
    website: "ycombinator.com",
    favicon: "https://bookface-static.ycombinator.com/assets/ycdc/favicon-c8a914eeeba9fe6f7a863b35608b55aeedd7c1ff409c97b9ecb96b7a6c278d70.ico",
    description: "Scrape Y Combinator's startup directory",
    type: "extract_list",
    isDemo: true,
    task: {
      name: "YC Startup Directory",
      task_type: "extract_list",
      request: {
        metadata: {
          url: "https://www.ycombinator.com/companies",
          title: "YC Startup Directory",
          favicon: "https://bookface-static.ycombinator.com/assets/ycdc/favicon-c8a914eeeba9fe6f7a863b35608b55aeedd7c1ff409c97b9ecb96b7a6c278d70.ico",
        },
        taskType: "extract_list",
        automation: {
          url: "https://www.ycombinator.com/companies",
          type: "extract_list",
          listSelectors: [
            { type: "xpath", value: `//div[a[starts-with(@href, '/companies/')]]` },
          ],
          paginationMethod: "scroll",
          paginationSelectors: [],
        },
        configuration: [
          {
            id: "pageUnblocker",
            config: {
              captcha: "error",
              overlay: "handle",
              hardBlock: "error",
              overrides: {
                datadome: "error",
                hcaptcha: "error",
                other_popup: "warn",
                recaptcha_v2: "error",
                recaptcha_v3: "error",
                consent_banner: "handle",
                custom_captcha: "error",
                country_chooser: "warn",
                age_verification: "warn",
                newsletter_popup: "warn",
                cloudflare_turnstile: "handle",
              },
              detectionMode: "text",
              handlerInputs: {
                country_chooser: { country: "" },
              },
              maxBlockersToProcess: 2,
              minDelayBetweenAttempts: 2000,
            },
            category: "pageUnblocker",
          },
          {
            id: "customCommands",
            config: {
              enabled: false,
              commands: [],
              onFailure: "continue",
              retryOptions: { delayMs: 2000, maxAttempts: 2 },
            },
            category: "beforeRun",
          },
          {
            id: "cookiesStorage",
            config: {
              cookies: [],
              syncCookies: true,
              localStorage: [],
              syncLocalStorage: true,
            },
            category: "cookiesStorage",
          },
          {
            id: "proxyConfiguration",
            config: {
              city: null,
              country: null,
              proxyType: "isp",
            },
            category: "browserLocation",
          },
          {
            id: "userAgentConfiguration",
            config: {
              preset: "current",
              detectedBrowser: {
                os: "Mac OS",
                name: "Chrome",
                viewport: { width: 1917, height: 1204 },
                userAgent:
                  "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/144.0.0.0 Safari/537.36",
              },
            },
            category: "userAgent",
          },
          {
            id: "dataCleaning",
            config: {
              removeEmptyRows: true,
              sparseThreshold: 0.7,
              removeEmptyColumns: true,
              repeatingThreshold: 0.999,
              removeSparseColumns: true,
              sortColumnsByDensity: true,
              removeDuplicateColumns: true,
              removeRepeatingColumns: true,
            },
            category: "dataCleaning",
          },
          {
            id: "googleSheetsExport",
            config: {
              mode: "create",
              enabled: false,
              sheetName: "Sheet1",
              includeHeader: true,
              spreadsheetId: null,
              spreadsheetUrl: null,
              spreadsheetName: null,
            },
            category: "integrations",
          },
          {
            id: "webhookExport",
            config: { enabled: false },
            category: "integrations",
          },
        ],
      },
    },
  },
  {
    id: "demo-google-maps-cafe",
    name: "Google Maps Cafes",
    website: "google.com/maps",
    favicon: "https://www.google.com/images/branding/product/ico/web_maps_icon_32dp.ico",
    description: "Scrape cafe listings from Google Maps",
    type: "extract_list",
    isDemo: true,
    task: {
      name: "cafe - Google Maps",
      task_type: "extract_list",
      request: {
        metadata: {
          url: "https://www.google.com/maps/search/cafe/@40.6965556,-74.1443114,11z/data=!3m1!4b1?entry=ttu&g_ep=EgoyMDI2MDMwMi4wIKXMDSoASAFQAw%3D%3D",
          title: "cafe - Google Maps",
          favicon: "https://www.google.com/images/branding/product/ico/web_maps_icon_32dp.ico",
        },
        taskType: "extract_list",
        automation: {
          url: "https://www.google.com/maps/search/cafe/@40.6965556,-74.1443114,11z/data=!3m1!4b1?entry=ttu&g_ep=EgoyMDI2MDMwMi4wIKXMDSoASAFQAw%3D%3D",
          type: "extract_list",
          listSelectors: [
            { type: "css", value: `div[role="feed"]` },
            { type: "xpath", value: `//div[@role="feed"]` },
            { type: "xpath", value: `//div[@aria-label="Results for cafe"]` },
            { type: "css", value: `div[aria-label="Results for cafe"]` },
          ],
          paginationMethod: "scroll",
          paginationSelectors: [],
        },
        configuration: [
          {
            id: "pageUnblocker",
            config: {
              captcha: "error",
              overlay: "handle",
              hardBlock: "error",
              overrides: {
                datadome: "error",
                hcaptcha: "error",
                other_popup: "warn",
                recaptcha_v2: "error",
                recaptcha_v3: "error",
                consent_banner: "handle",
                custom_captcha: "error",
                country_chooser: "warn",
                age_verification: "handle",
                newsletter_popup: "warn",
                cloudflare_turnstile: "handle",
              },
              detectionMode: "image",
              handlerInputs: {
                country_chooser: { country: "" },
              },
              maxBlockersToProcess: 2,
              minDelayBetweenAttempts: 2000,
            },
            category: "pageUnblocker",
          },
          {
            id: "cookiesStorage",
            config: {
              cookies: [],
              syncCookies: true,
              localStorage: [],
              syncLocalStorage: true,
            },
            category: "cookiesStorage",
          },
          {
            id: "proxyConfiguration",
            config: {
              city: null,
              country: { code: "JP", name: "Japan" },
              proxyType: "isp",
            },
            category: "browserLocation",
          },
          {
            id: "userAgentConfiguration",
            config: {
              preset: "current",
              detectedBrowser: {
                os: "Mac OS",
                name: "Chrome",
                viewport: { width: 1902, height: 1203 },
                userAgent:
                  "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/145.0.0.0 Safari/537.36",
              },
            },
            category: "userAgent",
          },
          {
            id: "dataCleaning",
            config: {
              removeEmptyRows: true,
              sparseThreshold: 0.7,
              removeEmptyColumns: true,
              repeatingThreshold: 0.999,
              removeSparseColumns: true,
              sortColumnsByDensity: true,
              removeDuplicateColumns: true,
              removeRepeatingColumns: true,
            },
            category: "dataCleaning",
          },
          {
            id: "googleSheetsExport",
            config: {
              mode: "create",
              enabled: false,
              sheetName: "Sheet1",
              includeHeader: true,
              spreadsheetId: null,
              spreadsheetUrl: null,
              spreadsheetName: null,
            },
            category: "integrations",
          },
          {
            id: "webhookExport",
            config: { enabled: false },
            category: "integrations",
          },
        ],
      },
    },
  },
  {
    id: "demo-ebay-refurbished-iphones",
    name: "eBay Refurbished iPhones",
    website: "ebay.com",
    favicon: "https://pages.ebay.com/favicon.ico",
    description: "Scrape refurbished iPhone listings from eBay",
    type: "extract_list",
    isDemo: true,
    task: {
      name: "Refurbished iPhones | eBay. With guarantee and at an excellent price.",
      task_type: "extract_list",
      request: {
        metadata: {
          url: "https://www.ebay.com/e/row/applerefurbishedrow",
          title: "Refurbished iPhones | eBay. With guarantee and at an excellent price.",
          favicon: "https://pages.ebay.com/favicon.ico",
        },
        taskType: "extract_list",
        automation: {
          url: "https://www.ebay.com/e/row/applerefurbishedrow",
          type: "extract_list",
          listSelectors: [
            { type: "css", value: "ol.dp-grid-collection-module__gallery-grid" },
            { type: "xpath", value: `//ol[count(li[.//a[contains(@href, '/itm/')]]) > 10]` },
          ],
          paginationMethod: "scroll",
          paginationSelectors: [],
        },
        configuration: [
          {
            id: "pageUnblocker",
            config: {
              captcha: "error",
              overlay: "handle",
              hardBlock: "error",
              overrides: {
                datadome: "error",
                hcaptcha: "error",
                other_popup: "warn",
                recaptcha_v2: "error",
                recaptcha_v3: "error",
                consent_banner: "handle",
                custom_captcha: "error",
                country_chooser: "warn",
                age_verification: "handle",
                newsletter_popup: "warn",
                cloudflare_turnstile: "handle",
              },
              detectionMode: "text",
              handlerInputs: {
                country_chooser: { country: "" },
              },
              maxBlockersToProcess: 2,
              minDelayBetweenAttempts: 2000,
            },
            category: "pageUnblocker",
          },
          {
            id: "cookiesStorage",
            config: {
              cookies: [],
              syncCookies: true,
              localStorage: [],
              syncLocalStorage: true,
            },
            category: "cookiesStorage",
          },
          {
            id: "proxyConfiguration",
            config: {
              city: null,
              country: { code: "US", name: "USA" },
              proxyType: "isp",
            },
            category: "browserLocation",
          },
          {
            id: "userAgentConfiguration",
            config: {
              preset: "current",
              detectedBrowser: {
                os: "Mac OS",
                name: "Chrome",
                viewport: { width: 1902, height: 1203 },
                userAgent:
                  "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/145.0.0.0 Safari/537.36",
              },
            },
            category: "userAgent",
          },
          {
            id: "dataCleaning",
            config: {
              removeEmptyRows: true,
              sparseThreshold: 0.7,
              removeEmptyColumns: true,
              repeatingThreshold: 0.999,
              removeSparseColumns: true,
              sortColumnsByDensity: true,
              removeDuplicateColumns: true,
              removeRepeatingColumns: true,
            },
            category: "dataCleaning",
          },
          {
            id: "googleSheetsExport",
            config: {
              mode: "create",
              enabled: false,
              sheetName: "Sheet1",
              includeHeader: true,
              spreadsheetId: null,
              spreadsheetUrl: null,
              spreadsheetName: null,
            },
            category: "integrations",
          },
          {
            id: "webhookExport",
            config: { enabled: false },
            category: "integrations",
          },
        ],
      },
    },
  },
];

/**
 * Get a demo by ID
 */
export const getDemoById = (id) => DEMO_AUTOMATIONS.find((d) => d.id === id);
